import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Layers, Send, Loader2, Plus, X, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import {
  CapturaDocumentos,
  subirDocumentosCapturados,
  type DocumentoCapturado,
} from "@/components/CapturaDocumentos";
import {
  Alert,
  Button,
  Card,
  DataField,
  FormField,
  Input,
  PageContainer,
  PageHeader,
  Textarea,
} from "@/components/ui";

interface CertificadoDetalle {
  id: string;
  numCertificado: string;
  fechaEmision: string;
  titularRazonSocial: string | null;
  productoNombre: string;
  productoModelo: string;
  productoModelos: string | null;
  marca: string;
}

type TipoCambio = "AMPLIACION" | "REDUCCION" | "MIXTO";
type Accion = "ALTA" | "BAJA";

interface Movimiento {
  modelo: string;
  accion: Accion;
}

const CONTEXTO_TIPO = "SOLICITUD_TELECOM";
const TAG_DOCS = "TELECOM_AMP_MODELOS";

/** Clave de comparación: trim + espacios colapsados + mayúsculas (espejo del backend). */
function normalizar(s: string): string {
  return s.trim().replace(/\s+/g, " ").toUpperCase();
}

/** Separa la lista de modelos adicionales (texto coma/; separado) preservando el texto. */
function parseModelos(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[,;]/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
}

/**
 * Pantalla dedicada de Ampliación / Reducción de Modelos o Productos (#9),
 * segundo trámite de la familia "Ampliación / Reducción". Se llega desde la
 * Selección de Certificado con ?cert=<certificadoId>.
 *
 * El alcance vigente del piloto es la lista de MODELOS ADICIONALES del
 * certificado (productoModelos); el "Modelo principal" se muestra como base de
 * solo lectura. La lista se precarga editable; al enviar se calcula el delta
 * contra el alcance original (lo agregado = ALTA, lo quitado = BAJA) y se deriva
 * el tipo de movimiento. El backend (crearAmpliacionModelos) recibe el contrato
 * movimientos[]. La carga de documentos forma parte de la captura.
 */
export function AmpliacionModelosAltaPage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const certId = params.get("cert") ?? "";

  const [modelos, setModelos] = useState<string[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [docs, setDocs] = useState<DocumentoCapturado[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Envío en curso + solicitud creada (para reintento de subida sin re-crear).
  const [enviando, setEnviando] = useState(false);
  const [solicitudCreada, setSolicitudCreada] = useState<string | null>(null);

  const qCert = useQuery<CertificadoDetalle>({
    queryKey: ["telecom-certificado", certId],
    queryFn: async () =>
      (await api.get<CertificadoDetalle>(`/api/sector/telecom/certificados/${certId}`)).data,
    enabled: access.allowed && !!certId,
  });

  // Alcance vigente de modelos adicionales (base contra la que se calcula el delta).
  const base = useMemo(() => parseModelos(qCert.data?.productoModelos), [qCert.data]);

  // Precargar el editor con el alcance actual una sola vez.
  useEffect(() => {
    if (!seeded && qCert.isSuccess) {
      setModelos(base);
      setSeeded(true);
    }
  }, [seeded, qCert.isSuccess, base]);

  // Delta calculado contra el alcance original → movimientos para el backend.
  const movimientos = useMemo<Movimiento[]>(() => {
    const baseKeys = new Set(base.map(normalizar));
    const actualKeys = new Set(modelos.map(normalizar));
    const mv: Movimiento[] = [];
    modelos.forEach((m) => {
      if (!baseKeys.has(normalizar(m))) mv.push({ modelo: m, accion: "ALTA" });
    });
    base.forEach((m) => {
      if (!actualKeys.has(normalizar(m))) mv.push({ modelo: m, accion: "BAJA" });
    });
    return mv;
  }, [modelos, base]);

  const hayAlta = movimientos.some((m) => m.accion === "ALTA");
  const hayBaja = movimientos.some((m) => m.accion === "BAJA");
  const tipoMovimiento: TipoCambio | null =
    hayAlta && hayBaja ? "MIXTO" : hayAlta ? "AMPLIACION" : hayBaja ? "REDUCCION" : null;
  const hayCambios = movimientos.length > 0;

  const agregar = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    if (modelos.some((m) => normalizar(m) === normalizar(t))) return; // dedup
    setModelos((arr) => [...arr, t]);
  };
  const quitar = (modelo: string) =>
    setModelos((arr) => arr.filter((m) => normalizar(m) !== normalizar(modelo)));

  const enviar = async () => {
    if (!hayCambios) {
      setError("No hay cambios en los modelos respecto al alcance actual del certificado.");
      return;
    }
    setError(null);
    setEnviando(true);
    try {
      let id = solicitudCreada;
      if (!id) {
        id = (
          await api.post<{ id: string }>("/api/sector/telecom/solicitudes/ampliacion-modelos", {
            certificadoBaseId: certId,
            tipoMovimiento,
            movimientos,
            motivo: motivo.trim() || null,
          })
        ).data.id;
        setSolicitudCreada(id);
      }
      const fallidos = docs.length
        ? await subirDocumentosCapturados(docs, setDocs, CONTEXTO_TIPO, id)
        : 0;
      if (fallidos > 0) {
        setError(
          `La solicitud se registró, pero ${fallidos} documento(s) no se pudieron subir. ` +
            `Reintenta los marcados en rojo o continúa y adjúntalos desde el detalle de la solicitud.`,
        );
        setEnviando(false);
        return;
      }
      navigate(`/sector/telecom/solicitudes/${id}/success`);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo registrar la solicitud.",
      );
      setEnviando(false);
    }
  };

  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }
  if (!certId) {
    return (
      <Alert variant="warning">
        Falta el certificado. Vuelve a la{" "}
        <Link to="/telecom/servicios/seleccionar-certificado?s=ampliacion-modelos" className="font-medium underline">
          selección de certificado
        </Link>.
      </Alert>
    );
  }

  const bloqueado = !!solicitudCreada;

  return (
    <PageContainer width="form">
      <PageHeader
        backTo="/telecom/servicios/seleccionar-certificado?s=ampliacion-modelos"
        backLabel="Cambiar certificado"
        icon={<Layers className="h-5 w-5" />}
        title="Ampliación / Reducción de Modelos o Productos"
        subtitle="Edita los modelos adicionales del alcance vigente; el sistema detecta automáticamente las altas y bajas."
      />

      <Alert variant="info">
        El Modelo principal y el resto del alcance (producto, normas, países) se conservan sin cambios.
      </Alert>

      <Card title="Certificado seleccionado">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando certificado…</div>
        ) : qCert.error || !qCert.data ? (
          <div className="text-sm text-estado-warn">No se pudo cargar el certificado seleccionado.</div>
        ) : (
          <dl className="grid gap-4 sm:grid-cols-2">
            <DataField label="Núm. de Certificado" value={<span className="font-mono">{qCert.data.numCertificado}</span>} />
            <DataField label="Razón Social" value={qCert.data.titularRazonSocial ?? "—"} />
            <DataField label="Producto" value={qCert.data.productoNombre} />
            <DataField label="Modelo principal" value={<span className="font-mono">{qCert.data.productoModelo}</span>} />
          </dl>
        )}
      </Card>

      {/* DICE: modelos adicionales actuales (solo lectura) */}
      <Card title="DICE — modelos adicionales actuales">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando alcance del certificado…</div>
        ) : base.length === 0 ? (
          <p className="text-sm text-fg-subtle">El certificado no tiene modelos adicionales registrados en su alcance.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {base.map((m) => (
              <span key={normalizar(m)} className="rounded bg-surface-2 px-2 py-1 font-mono text-sm text-fg-muted">
                {m}
              </span>
            ))}
          </div>
        )}
      </Card>

      {/* DEBE DECIR: editor de modelos */}
      <Card title="DEBE DECIR — modelos del nuevo alcance">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando alcance del certificado…</div>
        ) : (
          <ModelosEditor modelos={modelos} onAgregar={agregar} onQuitar={quitar} disabled={bloqueado} />
        )}

        {/* Resumen de cambios detectados */}
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            Cambios detectados{hayCambios ? ` (${movimientos.length})` : ""}
          </div>
          {!hayCambios ? (
            <p className="mt-1 text-sm text-fg-subtle">
              Aún no hay cambios respecto al alcance actual. Agrega o quita modelos arriba.
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {movimientos.map((m) => (
                <span
                  key={`${m.accion}-${normalizar(m.modelo)}`}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[11px] font-medium ${
                    m.accion === "ALTA" ? "bg-estado-ok-bg text-estado-ok" : "bg-estado-err-bg text-estado-err"
                  }`}
                >
                  {m.accion === "ALTA" ? "+" : "−"} {m.modelo}
                </span>
              ))}
            </div>
          )}
        </div>

        <FormField label="Justificación / comentarios" className="mt-4">
          <Textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            disabled={bloqueado}
            rows={3}
            placeholder="Explica el motivo del cambio de modelos (diferencias contra el modelo base, etc.)."
          />
        </FormField>
      </Card>

      {/* Documentos de soporte — parte de la captura */}
      <CapturaDocumentos
        tag={TAG_DOCS}
        titulo="Documentos de soporte"
        ayuda="Adjunta los documentos del trámite. Se subirán junto con la solicitud al enviarla."
        docs={docs}
        onChange={setDocs}
        disabled={enviando}
      />

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Link to="/telecom/servicios/seleccionar-certificado?s=ampliacion-modelos">
          <Button variant="secondary" className="w-full sm:w-auto">Regresar</Button>
        </Link>
        {bloqueado ? (
          <>
            <Button variant="secondary" loading={enviando} onClick={enviar} className="w-full sm:w-auto">
              Reintentar documentos
            </Button>
            <Button
              variant="success"
              icon={<CheckCircle2 className="h-4 w-4" />}
              onClick={() => navigate(`/sector/telecom/solicitudes/${solicitudCreada}/success`)}
              className="w-full sm:w-auto"
            >
              Continuar
            </Button>
          </>
        ) : (
          <Button
            variant="success"
            icon={<Send className="h-4 w-4" />}
            loading={enviando}
            disabled={!hayCambios || qCert.isLoading || !!qCert.error}
            onClick={enviar}
            className="w-full sm:w-auto"
          >
            Enviar solicitud
          </Button>
        )}
      </div>
    </PageContainer>
  );
}

/** Editor de chips de modelos (texto libre): input + agregar, lista con quitar. */
function ModelosEditor({
  modelos,
  onAgregar,
  onQuitar,
  disabled,
}: {
  modelos: string[];
  onAgregar: (m: string) => void;
  onQuitar: (m: string) => void;
  disabled?: boolean;
}) {
  const [valor, setValor] = useState("");
  const commit = () => {
    onAgregar(valor);
    setValor("");
  };
  return (
    <div className="space-y-3">
      <FormField label="Agregar modelo al alcance">
        <div className="flex gap-2">
          {/* RF-003: el modelo entra tal cual se escribe; la comparación con el
              alcance vigente se hace con normalizar(), no alterando el dato. */}
          <Input
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
            }}
            disabled={disabled}
            placeholder="Escribe un modelo y pulsa Enter o Agregar"
            className="font-mono"
          />
          <Button
            type="button"
            variant="secondary"
            icon={<Plus className="h-4 w-4" />}
            onClick={commit}
            disabled={disabled || !valor.trim()}
          >
            Agregar
          </Button>
        </div>
      </FormField>

      {modelos.length === 0 ? (
        <p className="text-sm text-fg-subtle">Sin modelos adicionales en el alcance.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {modelos.map((m) => (
            <span
              key={normalizar(m)}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary-strong/40 bg-primary-tint px-2.5 py-1 font-mono text-sm text-primary-strong"
            >
              {m}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onQuitar(m)}
                  className="cursor-pointer rounded-full p-0.5 text-primary-strong/60 transition hover:bg-primary-tint hover:text-primary-strong"
                  aria-label={`Quitar ${m}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
